import { OnChangeFn, Row, SortingState } from "@tanstack/react-table";
import classNames from "classnames";
import { useRef, useState } from "react";
import KuikaAvatar, { getIdentitySeed } from "../kuika-avatar/kuika-avatar";
import KuikaBadge from "../kuika-badge/kuika-badge";
import KuikaPopover from "../kuika-popover/kuika-popover";
import KuikaSmallSpinner from "../kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaTable, {
  KuikaTableColumn,
  KuikaTableDragDrop,
  KuikaTableEmptyVariant,
  KuikaTableLoadingVariant,
  KuikaTableTree
} from "../kuika-table/kuika-table";
import KuikaTooltip from "../kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../typography/SmallText/SmallText";
import KuikaTextMedium from "../typography/TextMedium/TextMedium";
import "./kuika-list-table.scss";

/**
 * Entity-list table: the "list of things you own" surface (processes, actions,
 * jobs…) — an icon-led name, a status badge, who/when columns, and a row-hover
 * actions menu.
 *
 * It COMPOSES `KuikaTable` rather than reimplementing a grid: sorting, column
 * filtering, resizing, loading and empty states already live there and already
 * match this design. What this component adds is the opinionated column defaults,
 * the row-actions menu, and the four repeated cell atoms below — so entity-list
 * screens stop hand-rolling them.
 */

export type KuikaListTableColumn<T> = KuikaTableColumn<T>;

export interface KuikaListTableRowAction<T> {
  key: string;
  label: string;
  icon?: React.ReactNode;
  /** Renders the item in the danger style (destructive actions). */
  danger?: boolean;
  /** Return true to omit this action for a given row. */
  hidden?: (row: T) => boolean;
  /** Overrides the id derived from `label`; see `rowActionTestId`. */
  dataTestId?: string;
  onSelect: (row: T) => void;
}

export interface KuikaListTableProps<T> {
  data: T[];
  columns: KuikaListTableColumn<T>[];
  rowKey: (row: T) => string;
  /** When provided, a trailing actions column is appended (visible on row hover). */
  rowActions?: KuikaListTableRowAction<T>[];
  /**
   * Optional `data-testid` for a row's actions trigger. Lets E2E target one row's menu by
   * a stable per-row id instead of an index or an internal class — the same reason
   * `KuikaTableColumn.headerTestId` exists. Wins over the `<id>-actions` that `rowTestId` derives.
   */
  rowActionsTestId?: (row: T) => string;
  /**
   * Optional `data-testid` per row, threaded to the row's `<tr>` AND used to name its actions:
   * the trigger becomes `<id>-actions` and each item `<id>-action-<key>`. Row actions live in a
   * popover that only exists while it is open, so without this the only way to reach one is by
   * its label text — which is exactly what breaks when a label is renamed or localized.
   */
  rowTestId?: (row: T) => string;
  /**
   * Per-row progress: return true while that row has work in flight. Its actions
   * trigger becomes a spinner, so a row-level mutation reports progress on the row
   * itself instead of blanking the whole table.
   */
  isRowBusy?: (row: T) => boolean;
  loading?: boolean;
  /** KuikaTable's: skeleton rows until the first rows arrive (default), or the spinner throughout. */
  loadingVariant?: KuikaTableLoadingVariant;
  onRowClick?: (row: T) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: React.ReactNode;
  /** KuikaTable's: `page` for a tabbed landing screen, the flat grid band otherwise. */
  emptyVariant?: KuikaTableEmptyVariant;
  /**
   * Entity lists are short by nature, so the frame ends at the last row by default
   * rather than padding out to KuikaTable's 400px floor. Set false to get that
   * fixed-height frame back.
   */
  fitContentHeight?: boolean;
  /**
   * Turns header sorting off. Use when a `tree` mixes kinds at the same level — a
   * folder row beside an unfiled item — so a header sort would interleave them.
   * The sort then belongs to the caller's own control.
   */
  disableSorting?: boolean;
  /**
   * Sorting the caller owns: pass all three when the row order is computed outside the
   * table. See `KuikaTable.manualSorting`.
   */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  /** Turns to the page holding this row, then calls `onRowRevealed`. See `KuikaTable`. */
  revealRowKey?: string | null;
  onRowRevealed?: () => void;
  /** Per-row class hook. `tableRow` carries `depth` for tree rows. */
  rowClassName?: (row: T, tableRow: Row<T>) => string | undefined;
  /** Renders the rows as a tree of expandable parents. See `KuikaTableTree`. */
  tree?: KuikaTableTree<T>;
  /** Renders a detail panel under an expanded row (spans the full width). */
  renderSubComponent?: (row: T) => React.ReactNode;
  /** Which rows offer the expander. Required for `renderSubComponent` to show. */
  getRowCanExpand?: (row: T) => boolean;
  /**
   * Drag rows between groups. Rows sharing a group id must be contiguous in `data`;
   * each run becomes its own drop target. See `KuikaTableDragDrop`.
   */
  dragDrop?: KuikaTableDragDrop<T>;
  className?: string;
  "data-testid"?: string;
}

/** Rows per page. `KuikaTable`'s own default, named here because the pager reads it too. */
const LIST_PAGE_SIZE: number = 10;

const ACTIONS_COLUMN_ID = "kuika-list-table-actions";
// The trigger is a 24px square, so the column only has to hold that plus a little
// air. It used to be 56 because the cell's own 16px side padding ate 32 of it —
// `__actions-cell` drops that padding, which is what lets the column come in.
const ACTIONS_COLUMN_WIDTH = 36;

/**
 * Menu entries carry the same derived hooks `KuikaDropdownMenu` gives its items, so a
 * list that moves from that menu into this table keeps its E2E selectors and its
 * onboarding anchors. Keep both derivations in step.
 */
const rowActionTestId = (label: string): string =>
  `dropdown${label
    .replace(/-/g, "")
    .replace(/^[^.]*\./, "")
    .replace(/\s/g, "")}Button`;

const rowActionOnboardingId = (label: string): string =>
  `ui-kit_dropdown-menu_${label}`.replace(/\s/g, "").toLowerCase();

interface RowActionsMenuProps<T> {
  row: T;
  actions: KuikaListTableRowAction<T>[];
  triggerTestId?: string;
  rowTestId?: string;
}

function RowActionsMenu<T>({ row, actions, triggerTestId, rowTestId }: RowActionsMenuProps<T>) {
  const [visible, setVisible] = useState<boolean>(false);
  const available: KuikaListTableRowAction<T>[] = actions.filter(
    (action: KuikaListTableRowAction<T>): boolean => !action.hidden?.(row)
  );
  if (available.length === 0) return null;

  return (
    <KuikaPopover
      visible={visible}
      onVisibleChange={setVisible}
      align="end"
      hideCloseIcon
      // The popover's default chrome is built for panels (vertical padding + gaps); a menu takes
      // the floating-list standard instead — inset, rounded rows, as KuikaDropdownMenu draws them.
      className="kuika-list-table__menu-popover"
      content={
        <div className="kuika-list-table__menu">
          {available.map((action: KuikaListTableRowAction<T>) => (
            <button
              key={action.key}
              type="button"
              // One id per item: an explicit `dataTestId`, else the `<rowTestId>-action-<key>` a
              // row-addressed list asks for, else the label-derived `dropdown<Label>Button`.
              data-testid={
                action.dataTestId ?? (rowTestId ? `${rowTestId}-action-${action.key}` : rowActionTestId(action.label))
              }
              data-onboarding-id={rowActionOnboardingId(action.label)}
              className={classNames("kuika-list-table__menu-item", {
                "kuika-list-table__menu-item--danger": action.danger
              })}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                // The row itself is clickable; an action must not also open it.
                e.stopPropagation();
                setVisible(false);
                action.onSelect(row);
              }}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      }
    >
      <button
        type="button"
        // `kuika_table_dot_menu` is KuikaTable's existing hook for controls that only appear on
        // row hover. This trigger hides by opacity rather than the hook's `visibility: hidden`, so
        // it stays a Tab stop (kuika-list-table.scss).
        className={classNames("kuika-list-table__more-btn", "kuika_table_dot_menu", {
          "kuika-list-table__more-btn--active": visible
        })}
        aria-label="Row actions"
        data-testid={triggerTestId ?? (rowTestId ? `${rowTestId}-actions` : undefined)}
        // No stopPropagation here: radix dismisses a popover from a `click` listener on the
        // document, so swallowing this one left the previous row's menu open beside the new
        // one. KuikaTable's own row-click guard already ignores clicks on a <button>.
      >
        <KuikaSvg name="dots" width={16} height={16} color="var(--dark-6)" />
      </button>
    </KuikaPopover>
  );
}

const KuikaListTable = <T extends object>({
  data,
  columns,
  rowKey,
  rowActions,
  rowActionsTestId,
  rowTestId,
  isRowBusy,
  loading,
  loadingVariant,
  onRowClick,
  emptyTitle,
  emptyDescription,
  emptyIcon,
  emptyVariant,
  fitContentHeight = true,
  disableSorting = false,
  sorting,
  onSortingChange,
  manualSorting,
  revealRowKey,
  onRowRevealed,
  rowClassName,
  tree,
  renderSubComponent,
  getRowCanExpand,
  dragDrop,
  className,
  "data-testid": dataTestId
}: KuikaListTableProps<T>) => {
  // Every entity-list column is sortable unless a column opts out, which is what
  // puts the neutral sort glyph beside each header label in the design.
  const withDefaults: KuikaListTableColumn<T>[] = columns.map(
    (column: KuikaListTableColumn<T>): KuikaListTableColumn<T> => ({
      enableSorting: !disableSorting,
      ...column,
      ...(disableSorting ? { enableSorting: false } : {})
    })
  );

  const allColumns: KuikaListTableColumn<T>[] =
    rowActions && rowActions.length > 0
      ? [
          ...withDefaults,
          {
            id: ACTIONS_COLUMN_ID,
            header: () => "",
            width: ACTIONS_COLUMN_WIDTH,
            // Centred, not right-aligned: the declared 36px is a minimum, and the table hands a
            // short row's leftover width to every column — so against the right edge the trigger
            // drifted away from the middle of a column that had grown to four times its ask.
            align: "center",
            enableSorting: false,
            cellClassName: (): string => "kuika-list-table__actions-cell",
            cell: (row: T) =>
              isRowBusy?.(row) ? (
                <span className="kuika-list-table__row-busy">
                  <KuikaSmallSpinner spinning />
                </span>
              ) : (
                <RowActionsMenu
                  row={row}
                  actions={rowActions}
                  triggerTestId={rowActionsTestId?.(row)}
                  rowTestId={rowTestId?.(row)}
                />
              )
          }
        ]
      : withDefaults;

  return (
    <div className={classNames("kuika-list-table", className)}>
      <KuikaTable<T>
        data={data}
        columns={allColumns}
        rowKey={rowKey}
        rowTestId={rowTestId}
        enableSorting={!disableSorting}
        sorting={sorting}
        onSortingChange={onSortingChange}
        manualSorting={manualSorting}
        sortIndicatorVariant="hover"
        rowClassName={rowClassName}
        tree={tree}
        enableExpanding={!!renderSubComponent || !!tree}
        renderSubComponent={renderSubComponent}
        getRowCanExpand={getRowCanExpand}
        dragDrop={dragDrop}
        loading={loading}
        loadingVariant={loadingVariant}
        // `KuikaTable` pages at 10 by default and draws its pager unconditionally, which put
        // a page control under every entity list — including an EMPTY one, where it offered
        // to leaf through nothing. These lists are short by nature (the same reason
        // `fitContentHeight` defaults on here and not there), so the pager appears only once
        // there is a second page to reach.
        enablePagination={data.length > LIST_PAGE_SIZE}
        pageSize={LIST_PAGE_SIZE}
        // Stay on the page the reader is on. Rows are built during render, and tanstack
        // resets the page index on a new `data` array — so opening a rename editor, or the
        // refetch that follows the rename, used to throw them back to page 1. KuikaTable
        // clamps the index when the rows shrink, so the page can never end up empty.
        autoResetPageIndex={false}
        revealRowKey={revealRowKey}
        onRowRevealed={onRowRevealed}
        fitContentHeight={fitContentHeight}
        // A landing's centre column can be narrower than its columns: they scroll sideways in the
        // frame instead of the trailing ones (the actions among them) being cut off. Drag-and-drop
        // lists too — the trade-off is confined to a frame too narrow for its columns: only then
        // is the scroller a scroll container, react-beautiful-dnd takes it for the droppables' one
        // scroll parent, and the page's auto-scroll during a drag may degrade.
        horizontalScroll
        onRowClick={onRowClick}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyIcon={emptyIcon}
        emptyVariant={emptyVariant}
        data-testid={dataTestId}
      />
    </div>
  );
};

export default KuikaListTable;

//  ────────────────────────────────────────────────────────────────────────────
//  Cell atoms — the repeated pieces of an entity-list row.
//  ────────────────────────────────────────────────────────────────────────────

export interface KuikaListTableNameCellProps {
  name: string;
  icon?: React.ReactNode;
  /**
   * Optional second line under the name — muted, 12px, ellipsised independently of the
   * name. Rows that omit it render exactly as before (single line, no height change);
   * rows that pass it grow the row to fit, the same way any taller cell content does
   * (see `--row-h-table` in kuika-table.scss — it's a minimum, not a fixed height).
   */
  secondary?: React.ReactNode;
  /** Hover text for a `secondary` that is not plain text (a code path, a badge) and may be clipped. */
  secondaryTitle?: string;
}

/**
 * Icon-led primary cell. The icon is placed in a tinted tile; retint per entity
 * type by setting `--kuika-list-table-icon-bg` / `--kuika-list-table-icon-color`
 * on the cell (with tokens — never raw hex).
 *
 * A name too long for its column is ellipsized, and then the whole name is only in the
 * tooltip: two rows sharing a prefix are otherwise the same cell. Measured on hover, so a
 * name that fits brings no tooltip, and a column resize needs no observer.
 */
/** What a row saved without a name reads as, so it still has something to be found and read by. */
export const KUIKA_LIST_TABLE_UNTITLED: string = "Untitled";

export const KuikaListTableNameCell = ({ name, icon, secondary, secondaryTitle }: KuikaListTableNameCellProps) => {
  const cellRef = useRef<HTMLSpanElement>(null);
  const isUntitled: boolean = !name || name.trim() === "";
  const [truncatedName, setTruncatedName] = useState<string>("");

  const measure = (): void => {
    const text: Element | null | undefined = cellRef.current?.querySelector(".kuika-list-table__name-text");
    const isCut: boolean = !!text && text.scrollWidth > text.clientWidth;
    setTruncatedName(isCut ? name : "");
  };

  return (
    <KuikaTooltip content={truncatedName || undefined} position="top" keepMounted>
      <span className="kuika-list-table__name-cell" ref={cellRef} onMouseEnter={measure}>
        {icon && <span className="kuika-list-table__name-icon">{icon}</span>}
        <span className="kuika-list-table__name-lines">
          <KuikaTextMedium
            className={
              isUntitled
                ? "kuika-list-table__name-text kuika-list-table__name-text--untitled"
                : "kuika-list-table__name-text"
            }
          >
            {isUntitled ? KUIKA_LIST_TABLE_UNTITLED : name}
          </KuikaTextMedium>
          {secondary && (
            <span
              className="kuika-list-table__name-secondary"
              // The second line ellipsises; a plain-text one can be read in full on hover.
              title={typeof secondary === "string" ? secondary : secondaryTitle}
            >
              {secondary}
            </span>
          )}
        </span>
      </span>
    </KuikaTooltip>
  );
};

export interface KuikaListTableStatusCellProps {
  label: string;
  color?: "blue" | "yellow" | "green" | "purple" | "orange" | "pink" | "grey" | "red";
  /** Tooltip text for a trailing info affordance (e.g. why a row is inactive). */
  hint?: string;
  /**
   * Optional `data-testid`. The label IS the state, so a caller that toggles a row's status
   * needs somewhere to read it back from — the badge itself carries no hook, and colour is not
   * assertable.
   */
  "data-testid"?: string;
  /**
   * Animates this row's status changing in place (colour, label, width) — see
   * `KuikaBadge.animateChanges`. Only where the same row's status changes while the user watches
   * (toggling a job, verifying a domain); leave it off in lists that sort or page.
   */
  animateChanges?: boolean;
}

/** Status badge, optionally followed by an info affordance. */
export const KuikaListTableStatusCell = ({
  label,
  color = "grey",
  hint,
  "data-testid": dataTestId,
  animateChanges = false
}: KuikaListTableStatusCellProps): JSX.Element => (
  <span className="kuika-list-table__status-cell" data-testid={dataTestId}>
    <KuikaBadge type="label" size="small" color={color} content={label} animateChanges={animateChanges} />
    {hint && (
      <KuikaTooltip content={hint} position="top" contentClassName="kuika-list-table__status-tooltip">
        <span className="kuika-list-table__status-hint">
          <KuikaSvg name="info-circle" width={13} height={13} color="var(--dark-5)" />
        </span>
      </KuikaTooltip>
    )}
  </span>
);

export interface KuikaListTableUserCellProps {
  /** Text beside the avatar — typically an already-formatted relative time. */
  caption?: string;
  /** Full name, shown as the avatar tooltip. */
  name?: string;
  photoUrl?: string;
  /** Shown inside the avatar when there is no photo. */
  initials?: string;
  /**
   * Who this is, for the colour of the photoless circle — an id, or the address. Without it
   * the circle is the neutral one, which is right for a row whose person is unknown and
   * wrong for one whose person simply has no photo.
   */
  seed?: string | null;
  /** Rendered when there is neither a caption nor any identity to show. */
  fallback?: string;
}

/**
 * Avatar + caption ("who and when"). Degrades in place: a photo when there is one,
 * initials when there is a name, a neutral glyph when the identity is unknown, and
 * `fallback` when there is no data at all.
 */
export const KuikaListTableUserCell = ({
  caption,
  name,
  photoUrl,
  initials,
  seed,
  fallback = "—"
}: KuikaListTableUserCellProps) => {
  if (!caption && !name && !photoUrl && !initials) {
    return <KuikaSmallText className="kuika-list-table__muted">{fallback}</KuikaSmallText>;
  }

  const avatar: React.ReactNode = (() => {
    if (photoUrl || initials) {
      return (
        <KuikaAvatar
          size={20}
          src={photoUrl}
          alt={name ?? ""}
          seed={seed ?? getIdentitySeed({ fullName: name })}
          className="kuika-list-table__avatar"
        >
          {initials}
        </KuikaAvatar>
      );
    }
    // Nobody to colour: this row's person is unknown, not merely photoless.
    return (
      <span className="kuika-list-table__avatar kuika-list-table__avatar--unknown">
        <KuikaSvg name="user" width={12} height={12} color="var(--white)" />
      </span>
    );
  })();

  return (
    <span className="kuika-list-table__user-cell">
      {name ? (
        <KuikaTooltip content={name} position="top">
          {avatar}
        </KuikaTooltip>
      ) : (
        avatar
      )}
      {caption && <KuikaSmallText className="kuika-list-table__muted">{caption}</KuikaSmallText>}
    </span>
  );
};
